import { el } from '../utils/dom.js';
import { SIZE_PRESETS } from '../generators/GeneratorRegistry.js';
import { ENGINE_PRESETS } from './EnginePresets.js';

/**
 * New map dialog — create a blank map with configurable dimensions.
 * Includes engine preset dropdown that auto-fills tile size and stores targetEngine on state.
 */
export class NewMapDialog {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
  }

  show() {
    const overlay = el('div', { className: 'dialog-overlay' });
    const dialog = el('div', { className: 'dialog' });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'New Map'));

    // Target engine preset
    const engineGroup = el('div', { className: 'form-group' });
    engineGroup.appendChild(el('label', { className: 'form-label' }, 'Target Engine'));
    const engineSelect = el('select', { className: 'form-input' });
    for (const [key, preset] of Object.entries(ENGINE_PRESETS)) {
      engineSelect.appendChild(el('option', { value: key }, preset.label));
    }
    engineGroup.appendChild(engineSelect);

    // Engine note text
    const engineNote = el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px', minHeight: '16px' },
    });
    engineGroup.appendChild(engineNote);
    dialog.appendChild(engineGroup);

    // Size preset
    const presetGroup = el('div', { className: 'form-group' });
    presetGroup.appendChild(el('label', { className: 'form-label' }, 'Size Preset'));
    const presetSelect = el('select', { className: 'form-input' });
    presetSelect.appendChild(el('option', { value: 'custom' }, 'Custom'));
    for (const [key, preset] of Object.entries(SIZE_PRESETS)) {
      presetSelect.appendChild(el('option', { value: key }, preset.label));
    }
    presetGroup.appendChild(presetSelect);
    dialog.appendChild(presetGroup);

    // Dimensions
    const dimRow = el('div', { className: 'form-row' });
    const widthField = el('div', { className: 'form-field' });
    widthField.append(el('label', { className: 'form-label-sm' }, 'Width (tiles)'));
    const widthInput = el('input', { type: 'number', className: 'form-input', value: '40', min: '5', max: '2000' });
    widthField.appendChild(widthInput);
    dimRow.appendChild(widthField);

    const heightField = el('div', { className: 'form-field' });
    heightField.append(el('label', { className: 'form-label-sm' }, 'Height (tiles)'));
    const heightInput = el('input', { type: 'number', className: 'form-input', value: '30', min: '5', max: '2000' });
    heightField.appendChild(heightInput);
    dimRow.appendChild(heightField);
    dialog.appendChild(dimRow);

    // Tile size
    const tileRow = el('div', { className: 'form-row' });
    const twField = el('div', { className: 'form-field' });
    twField.append(el('label', { className: 'form-label-sm' }, 'Tile Width'));
    const twInput = el('input', { type: 'number', className: 'form-input', value: '32', min: '8', max: '128' });
    twField.appendChild(twInput);
    tileRow.appendChild(twField);

    const thField = el('div', { className: 'form-field' });
    thField.append(el('label', { className: 'form-label-sm' }, 'Tile Height'));
    const thInput = el('input', { type: 'number', className: 'form-input', value: '32', min: '8', max: '128' });
    thField.appendChild(thInput);
    tileRow.appendChild(thField);
    dialog.appendChild(tileRow);

    // Engine tile size selector (shows preset tile size options)
    const tileSizeRow = el('div', { className: 'form-group', style: { display: 'none' } });
    tileSizeRow.appendChild(el('label', { className: 'form-label' }, 'Engine Tile Size'));
    const tileSizeSelect = el('select', { className: 'form-input' });
    tileSizeRow.appendChild(tileSizeSelect);
    dialog.appendChild(tileSizeRow);

    // Sync engine preset → tile size inputs
    const updateEnginePreset = () => {
      const preset = ENGINE_PRESETS[engineSelect.value];
      if (!preset || engineSelect.value === 'custom') {
        tileSizeRow.style.display = 'none';
        engineNote.textContent = '';
        return;
      }

      engineNote.textContent = preset.notes;

      // Drakkar presets: lock to 200x200 grid
      if (preset.drakkar) {
        widthInput.value = '200';
        heightInput.value = '200';
        widthInput.disabled = true;
        heightInput.disabled = true;
        presetSelect.disabled = true;
      } else {
        widthInput.disabled = false;
        heightInput.disabled = false;
        presetSelect.disabled = false;
      }

      if (preset.tileSizes.length > 0) {
        tileSizeRow.style.display = '';
        tileSizeSelect.innerHTML = '';
        for (const size of preset.tileSizes) {
          tileSizeSelect.appendChild(el('option', { value: size }, `${size}x${size}`));
        }
        if (preset.defaultTileSize) {
          tileSizeSelect.value = String(preset.defaultTileSize);
        }
        twInput.value = tileSizeSelect.value;
        thInput.value = tileSizeSelect.value;
      } else {
        tileSizeRow.style.display = 'none';
      }
    };

    engineSelect.addEventListener('change', updateEnginePreset);
    tileSizeSelect.addEventListener('change', () => {
      twInput.value = tileSizeSelect.value;
      thInput.value = tileSizeSelect.value;
    });

    // Sync size preset → dimension inputs
    presetSelect.addEventListener('change', () => {
      const preset = SIZE_PRESETS[presetSelect.value];
      if (preset) {
        widthInput.value = preset.width;
        heightInput.value = preset.height;
      }
    });

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const createBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      const w = parseInt(widthInput.value, 10) || 40;
      const h = parseInt(heightInput.value, 10) || 30;
      const tw = parseInt(twInput.value, 10) || 32;
      const th = parseInt(thInput.value, 10) || 32;
      this.state.createBlankMap(w, h, tw, th);

      // Store target engine on state
      const engine = engineSelect.value !== 'custom' ? engineSelect.value : null;
      this.state.set('targetEngine', engine);

      this.mapCanvas.clear();
      this.mapCanvas.invalidateAll();
      this.compositor.requestRender();
      overlay.remove();
    }}, 'Create');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(createBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }
}
